/**
 * SMS Grup Filo Excel raporları - Tasarım V2
 * Axios'un mevcut baseURL/Authorization interceptor ayarlarını kullanır.
 * Şablonu tarayıcıda oluşturmaz; FastAPI'nin hazırladığı XLSX dosyasını indirir.
 */
import api from '../../../api/axiosConfig';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const VEHICLE_EXCEL_REPORTS = Object.freeze({
  active: {
    title: 'Aktif Araç Listesi',
    url: '/vehicles/reports/vehicles/excel',
    fileStem: 'Aktif_Arac_Listesi',
  },
  archive: {
    title: 'Arşiv Araç Listesi',
    url: '/vehicles/reports/archive/excel',
    fileStem: 'Arsiv_Arac_Listesi',
  },
  maintenance: {
    title: 'Bakım Takvimi ve Geçmişi',
    url: '/vehicles/reports/maintenance-calendar/excel',
    fileStem: 'Bakim_Takvimi',
  },
  inspection: {
    title: 'Muayene Uyarıları',
    url: '/vehicles/reports/alerts/inspection/excel',
    fileStem: 'Muayene_Uyarilari',
  },
  insurance: {
    title: 'Trafik Sigortası Uyarıları',
    url: '/vehicles/reports/alerts/insurance/excel',
    fileStem: 'Sigorta_Uyarilari',
  },
  casco: {
    title: 'Kasko Uyarıları',
    url: '/vehicles/reports/alerts/casco/excel',
    fileStem: 'Kasko_Uyarilari',
  },
});

function getFilename(headers, fallback) {
  const disposition = headers?.['content-disposition'] || '';
  const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  const plainMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  let name = fallback;
  try {
    if (utf8Match) name = decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    else if (plainMatch) name = plainMatch[1].trim();
  } catch {
    name = fallback;
  }
  // Sunucudan gelen dosya adındaki tehlikeli yol/özel karakterleri temizle.
  const safe = name.replace(/[\\/\x00-\x1F<>:"|?*]/g, '_').trim();
  return safe.toLowerCase().endsWith('.xlsx') ? safe : fallback;
}

async function extractError(error) {
  const response = error?.response;
  if (response?.status === 401) return 'Oturum süresi dolmuş. Tekrar giriş yapın.';
  if (response?.status === 403) return 'Bu raporu indirmeye yetkiniz yok.';
  if (response?.status === 404) return 'Excel endpointi bulunamadı. Backend Tasarım V2 kurulumu ve servis yeniden başlatmasını kontrol edin.';
  if (response?.data instanceof Blob) {
    try {
      const message = JSON.parse(await response.data.text());
      if (typeof message.detail === 'string') return message.detail;
    } catch { /* API JSON göndermemiş olabilir. */ }
  }
  return error?.message || 'Excel dosyası indirilemedi. Sunucu ve ağ bağlantısını kontrol edin.';
}

/**
 * @param {'active'|'archive'|'maintenance'|'inspection'|'insurance'|'casco'} reportKey
 * @param {{ days?: number, includeArchived?: boolean }} options
 * @returns {Promise<string>} İndirilen dosyanın adı
 */
export async function downloadVehicleExcel(reportKey, options = {}) {
  const report = VEHICLE_EXCEL_REPORTS[reportKey];
  if (!report) throw new Error('Bilinmeyen araç filo raporu.');

  const params = {};
  if (['inspection', 'insurance', 'casco'].includes(reportKey) && options.days != null) {
    if (!Number.isInteger(options.days) || options.days < 0 || options.days > 365) {
      throw new Error('Uyarı süresi 0-365 gün arasında bir tam sayı olmalıdır.');
    }
    params.days = options.days;
  }
  if (reportKey === 'maintenance' && options.includeArchived != null) {
    params.include_archived = Boolean(options.includeArchived);
  }

  let response;
  try {
    response = await api.get(report.url, {
      responseType: 'blob',
      params,
      headers: { Accept: XLSX_MIME },
    });
  } catch (error) {
    throw new Error(await extractError(error));
  }

  const contentType = String(response.headers?.['content-type'] || response.data?.type || '').toLowerCase();
  if (contentType && !contentType.includes('spreadsheetml.sheet') && !contentType.includes('octet-stream')) {
    throw new Error('Sunucu Excel yerine farklı bir içerik döndürdü. Backend endpointini kontrol edin.');
  }
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: XLSX_MIME });
  if (!blob.size) throw new Error('Sunucudan boş bir Excel dosyası döndü.');

  // Başlık erişilebilir olduğunda eski sürümün dosyasını kaydetmeye izin verme.
  // Cross-origin isteklerde custom response header CORS yüzünden görünmeyebilir.
  const templateVersion = response.headers?.['x-excel-template-version'];
  if (templateVersion && templateVersion !== 'SMS-FILO-V2') {
    throw new Error(`Excel şablonu eski sürümde (${templateVersion}). Backend'i V2 olarak güncelleyin.`);
  }

  const date = new Date();
  const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const fallback = `SMS_Grup_${report.fileStem}_V2_${stamp}.xlsx`;
  const filename = getFilename(response.headers, fallback);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Tarayıcının indirilen blob'a erişimini kesmeden önce kısa süre bırak.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
  return filename;
}
